import {
  useApproveBills,
  useUpdateBillingConfiguration,
} from "@features/bx/BulkBillGeneration/hooks";
import { AlertBanner } from "@shared/components/CustomBanner";
import { DynamicForm } from "@shared/components/DynamicForm";
import { StepHelpers } from "@shared/components/Stepper";
import { toast } from "@shared/hooks/use-toast";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { ArrowLeft, CheckCircle, Save } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
interface Step6ApproveBillsProps {
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  data?: any;
  onDataChange?: (data: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
  allStepData?: Record<number, any>;
}
const Step6ApproveBills: React.FC<Step6ApproveBillsProps> = ({
  stepHelpers,
  currentStepIndex = 2,
  data = {},
  onDataChange,
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep,
  setValidationError,
  clearValidationError,
  isValidating,
  allStepData,
}) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { id, cycleId, runId } = useParams(); // this is runId
  const approveBills = useApproveBills(runId);
  const { mutateAsync: updateBillingCycle, isPending: isUpdating } =
    useUpdateBillingConfiguration(id);
  // Form fields configuration
  const formFields: FormField[] = useMemo(
    () => [
      // Data & Calculation Verification Group
      {
        name: "consumerDataAccuracy",
        type: "checkbox",
        label: "Consumer Data Accuracy",
        required: true,
        group: "Data & Calculation Verification",
        groupColor: "border-l-blue-500",
        groupHelperText:
          "Confirm accuracy of all billing data and calculations",
        helperText:
          "All consumer account details, service information, and contact details are accurate and complete.",
        classes: {
          container: "mb-4",
          label: "font-medium text-gray-900",
          helperText: "text-sm text-gray-600 mt-1",
        },
      },
      {
        name: "meterReadingValidation",
        type: "checkbox",
        label: "Meter Reading Validation",
        required: true,
        group: "Data & Calculation Verification",
        helperText:
          "All meter readings are collected, validated, and anomalies have been reviewed and resolved.",
        classes: {
          container: "mb-4",
          label: "font-medium text-gray-900",
          helperText: "text-sm text-gray-600 mt-1",
        },
      },
      {
        name: "tariffRateApplication",
        type: "checkbox",
        label: "Tariff & Rate Application",
        required: true,
        group: "Data & Calculation Verification",
        helperText:
          "All applicable tariffs, rates, and service charges are correctly applied to consumer accounts.",
        classes: {
          container: "mb-4",
          label: "font-medium text-gray-900",
          helperText: "text-sm text-gray-600 mt-1",
        },
      },
      {
        name: "billCalculationAccuracy",
        type: "checkbox",
        label: "Bill Calculation Accuracy",
        required: true,
        group: "Data & Calculation Verification",
        helperText:
          "Bill calculations are correct and comply with applicable tariffs and regulations.",
        classes: {
          container: "mb-4",
          label: "font-medium text-gray-900",
          helperText: "text-sm text-gray-600 mt-1",
        },
      },
      {
        name: "adjustmentsCredits",
        type: "checkbox",
        label: "Adjustments & Credits",
        group: "Data & Calculation Verification",
        helperText:
          "All adjustments, credits, and special charges are correctly applied to appropriate accounts.",
        classes: {
          container: "mb-4",
          label: "font-medium text-gray-900",
          helperText: "text-sm text-gray-600 mt-1",
        },
      },

      // Billing Execution & Authorization Group
      {
        name: "billDistributionAuth",
        type: "checkbox",
        label: "Bill Distribution Authorization",
        required: true,
        group: "Billing Execution & Authorization",
        groupColor: "border-l-green-500",
        groupHelperText: "Authorize distribution and processing of bills",
        helperText:
          "Authorize distribution of bills through all configured channels (email, SMS, print, in-app).",
        classes: {
          container: "mb-4",
          label: "font-medium text-gray-900",
          helperText: "text-sm text-gray-600 mt-1",
        },
      },
      {
        name: "paymentProcessingAuth",
        type: "checkbox",
        label: "Payment Processing Authorization",
        required: true,
        group: "Billing Execution & Authorization",
        helperText:
          "Authorize payment processing and automatic deductions for consumers with auto-pay enabled.",
        classes: {
          container: "mb-4",
          label: "font-medium text-gray-900",
          helperText: "text-sm text-gray-600 mt-1",
        },
      },
      {
        name: "consumerCommunicationAuth",
        type: "checkbox",
        label: "Consumer Communication Authorization",
        required: true,
        group: "Billing Execution & Authorization",
        helperText:
          "Authorize sending billing notifications, payment reminders, and related communications.",
        classes: {
          container: "mb-4",
          label: "font-medium text-gray-900",
          helperText: "text-sm text-gray-600 mt-1",
        },
      },
      {
        name: "finalApproval",
        type: "checkbox",
        label: "Final Billing Cycle Approval",
        required: true,
        group: "Billing Execution & Authorization",
        helperText:
          "I approve this billing cycle. Bills will be finalized and locked for audit purposes.",
        classes: {
          container: "p-3 bg-green-50 border border-green-200 rounded-lg mb-4",
          label: "font-medium text-green-900",
          helperText: "text-sm text-green-700 mt-1",
        },
      },

      // Approval Comments
      {
        name: "approvalComments",
        type: "textarea",
        label: "Approval Comments (Optional)",
        required: false,
        placeholder: "Enter any comments, notes, or special instructions...",
        rows: 5,
        helperText:
          "Add any notes or special instructions for this billing cycle",
        defaultValue: "",
        fullWidth: true,
        classes: {
          container: "w-full",
          label: "block text-lg font-semibold text-gray-900 mb-1",
          textarea:
            "w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-white resize-none",
          helperText: "text-sm text-gray-600 mt-1",
        },
      },
    ],
    []
  );

  // Create form instance
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
  });

  // Watch form values to check if all required checkboxes are checked
  const formValues = form.watch();
  const canApprove = useMemo(() => {
    return (
      formValues.consumerDataAccuracy &&
      formValues.meterReadingValidation &&
      formValues.tariffRateApplication &&
      formValues.billCalculationAccuracy &&
      formValues.adjustmentsCredits &&
      formValues.billDistributionAuth &&
      formValues.paymentProcessingAuth &&
      formValues.consumerCommunicationAuth &&
      formValues.finalApproval
    );
  }, [formValues]);

  // Handle back to summary
  const handleBackToSummary = () => {
    onPrevious?.();
  };

  // Handle save as draft
  const handleSaveAsDraft = async () => {
    setIsLoading(true);

    const payload = {
      step: {
        step_working_on: 5,
        total_steps: 5,
      },
    };
    const response = await updateBillingCycle(payload);
    if (response) {
      navigate("/bx/create-bill-generation");
    }
  };

  // Handle approve and finalize
  const handleApproveAndFinalize = async () => {
    if (!canApprove) {
      toast({
        title:
          "Please complete all verification items and authorizations before finalizing.",
        variant: "destructive",
      });
      return;
    }

    const isValid = await FormService.validateForm(form);
    if (!isValid) {
      toast({
        title: "Please complete all required verification items.",
        variant: "destructive",
      });
      return;
    }

    const formData = form.getValues();
    approveBills.mutate({
      cycle_id: cycleId,
      consents: [
        "consumer_data_accuracy",
        "meter_reading_validation",
        "tariff_rate_application",
        "bill_calculation_accuracy",
        "adjustments_credits",
        "bill_distribution_authorization",
        "payment_processing_authorization",
        "consumer_communication_authorization",
        "final_billing_cycle_approval",
      ],
      draft_id: id,
      comment: formData.approvalComments,
    });
  };

  // Custom footer renderer
  const renderCustomFooter = () => (
    <div className="flex items-center justify-between pt-6 pb-4 border-t border-gray-200 mt-6">
      <Button
        variant="ghost"
        onClick={handleBackToSummary}
        disabled={isLoading}
        className="flex items-center gap-2"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Summary
      </Button>

      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          onClick={handleSaveAsDraft}
          disabled={isLoading}
          className="flex items-center gap-2"
        >
          <Save className="h-4 w-4" />
          Save As Draft
        </Button>

        <Button
          onClick={handleApproveAndFinalize}
          disabled={!canApprove || isLoading}
          isLoading={isLoading}
          className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
        >
          <CheckCircle className="h-4 w-4" />
          Approve & Finalize Bills
        </Button>
      </div>
    </div>
  );

  return (
    <div className="p-4">
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <CheckCircle className="h-6 w-6 text-green-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Final Approval</h1>
            <p className="text-sm text-gray-600">
              Review and approve bills for distribution
            </p>
          </div>
        </div>

        {/* Important Notice Banner */}
        <AlertBanner
          variant="info"
          message="Once approved, bills will be finalized and locked for audit purposes. Please ensure all verification items are completed before proceeding."
        />

        {/* Dynamic Form */}
        <DynamicForm
          fields={formFields}
          form={form}
          config={{
            showSubmitButton: false,
            showResetButton: false,
            layout: "vertical",
            spacing: "medium",
          }}
          renderCustomFooter={renderCustomFooter}
          className="space-y-6"
        />
      </div>
    </div>
  );
};

export default Step6ApproveBills;
